import { BaseControl } from '@Common/Controls/BaseControl';
import constants from '@Common/Core/Constant';
import { destroyEvents } from '@Common/Core/Destroyable';
import pointerHelper from '@Common/Helpers/PointerHelper';

export interface IDragAndDropArgs {
    dataObjects: any[];
    drag: DragAndDrop;
    dragContainer: DragAndDrop;
    event: JQuery.Event;
    objectFrom: any;
    objectTo: any;
    uiObjects: HTMLElement[];
}

export interface IDragAndDropParam {
    clearOnEsc: boolean;
    cursorAt: { top: number; left: number };
    distance: number;
    canDrag: boolean;
    canDrop: boolean;
    greedy: boolean;
    dragContainment: JQuery | string;
    showContainer: boolean;
    scrollArea: any;
    iScroll: any;
    opacity: number;
    scopes: string[];
    refreshPositions: boolean;
}

export class DragAndDrop extends BaseControl<IDragAndDropParam> {
    private _onDrag = _.noop;
    private _onDragStart = _.noop;
    private _onDragStop = _.noop;
    private _onDragOver = _.noop;
    private _onDragOut = _.noop;
    private _onDrop = _.noop;
    private _onDropOver = _.noop;
    private _onPreDrag = _.noop;
    private readonly _clearOnEsc: boolean;
    private _body: JQuery;
    private _controls = [];
    private _autoScrollClass;
    private _intervalId;
    private _dragControl: JQuery;
    private _dragDescription: JQuery;
    private _dropOverData: any;
    private _dropParam: any;
    private _dropOverFunction: (event) => void;

    constructor(param: Partial<IDragAndDropParam>) {
        super();
        this._clearOnEsc = param.clearOnEsc;

        this._param = _.assign(
            {
                css: null,
                className: '',
                width: '100%',
                height: 'auto',
                clearOnEsc: false,
                cursorAt: { top: -5, left: -5 },
                distance: 10,
                canDrag: false,
                canDrop: false,
                greedy: false,
                dragContainment: 'window',
                showContainer: true,
                opacity: 1,
                scrollArea: null,
                iScroll: null,

                scopes: null,
                refreshPositions: false
            },
            param
        );

        this._body = $('body');
        this._init();
    }

    protected _init() {
        this._root = $('<div class="dragging-container"/>');

        if (this._clearOnEsc) {
            $(document).on('keydown', e => {
                const code = e.keyCode ? e.keyCode : e.which;

                if (code == constants.keyCodes.ESCAPE) {
                    this._root.empty();
                }
            });
        }
    }

    private _removeCursorStyle(): void {
        this._body.removeClass(
            'nw-scroll-cursor ne-scroll-cursor sw-scroll-cursor se-scroll-cursor w-scroll-cursor e-scroll-cursor n-scroll-cursor s-scroll-cursor'
        );
    }

    // eslint-disable-next-line complexity
    private _autoScroll(control, position, droppable?): void {
        const containerWidth = this._param.scrollArea.width();
        const containerHeight = this._param.scrollArea.height();
        const containerOffset = this._param.scrollArea.offset();

        const left = containerOffset.left;
        const top = containerOffset.top;
        const right = containerOffset.left + containerWidth;
        const bottom = containerOffset.top + containerHeight;

        const leftOffset = position.helper.offset().left - left;
        const rightOffset = right - position.helper.offset().left;
        const topOffset = position.helper.offset().top - top;
        const bottomOffset = bottom - position.helper.offset().top + position.helper.height();

        const inBounds = leftOffset >= 0 && rightOffset >= 0 && topOffset >= 0 && bottomOffset >= 0;

        const rightBoundExceeded = inBounds && this._param.iScroll.x > this._param.iScroll.maxScrollX && rightOffset < constants.autoScrollGap;

        const leftBoundExceeded = inBounds && this._param.iScroll.x < 0 && leftOffset < constants.autoScrollGap;

        const topBoundExceeded = inBounds && this._param.iScroll.y < 0 && topOffset < constants.autoScrollGap;

        const bottomBoundExceeded = inBounds && this._param.iScroll.y > this._param.iScroll.maxScrollY && bottomOffset < constants.autoScrollGap;

        this._autoScrollClass = pointerHelper.getAutoScrollCursor(leftBoundExceeded, rightBoundExceeded, topBoundExceeded, bottomBoundExceeded);
        this._body.addClass(this._autoScrollClass);

        if (topBoundExceeded && leftBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(-constants.autoScrollStep, -constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (topBoundExceeded && rightBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(constants.autoScrollStep, -constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (bottomBoundExceeded && leftBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(-constants.autoScrollStep, constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (bottomBoundExceeded && rightBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(constants.autoScrollStep, constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (rightBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(constants.autoScrollStep, 0, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (leftBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(-constants.autoScrollStep, 0, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (topBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(0, -constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else if (bottomBoundExceeded) {
            !droppable && control.draggable('option', 'refreshPositions', true);

            this._param.iScroll.scrollTo(0, constants.autoScrollStep, constants.autoScrollDelay, true, () =>
                this._autoScroll(control, position, droppable)
            );
        } else {
            if (!droppable && control.draggable('option', 'refreshPositions') === true) {
                setTimeout(() => {
                    control.draggable('option', 'refreshPositions', false);
                }, constants.autoScrollDelay);
            }

            this._removeCursorStyle();
        }
    }

    /**
     * Bind draggable effect to control
     * @param control - ui instance for dragg
     * @param item - date of object which dragg
     */
    public drag(control, item): void {
        let dragObjects;

        this._controls.push(control);

        control.draggable({
            refreshPositions: this._param.refreshPositions,
            distance: this._param.distance,
            cursorAt: this._param.cursorAt,
            opacity: this._param.opacity,
            cancel: null,
            containment: this._param.dragContainment || 'window',
            appendTo: 'body',
            scroll: false,
            start: () => this._onDragStart(dragObjects),
            drag: (ui, position) => {
                this.fixTarget(ui);
                if (this._param.scrollArea && $.contains(this._param.scrollArea[0], ui.toElement) && this._param.iScroll) {
                    this._autoScroll(control, position);
                }

                dragObjects.event = ui;
                this._onDrag(dragObjects);
            },
            stop: () => {
                this._onDragStop(dragObjects);
                this._removeCursorStyle();
            },
            helper: ui => {
                this.fixTarget(ui);
                const dd = this._onPreDrag(control, item);
                dragObjects = $.extend(
                    {
                        event: ui,
                        dataObjects: item ? [item] : [],
                        uiObjects: $(ui.currentTarget),
                        drag: this
                    },
                    dd
                );

                dragObjects.objectFrom = item || null;

                this._root.empty();
                this._root.data('dragObjects', dragObjects);

                const dragItems = this.createDraggingControl(dragObjects);
                if ($.isArray(dragItems)) {
                    _(dragItems).forEach(i => this._root.prepend(i));
                } else {
                    this._root.prepend(dragItems);
                }

                dragObjects.dragContainer = this;
                dragObjects.scopes = this._param.scopes;

                return this._root;
            }
        });
    }

    private _checkScopes(scopes: string[]): any {
        return _(scopes).some(i => _(this._param.scopes).some(n => i == n));
    }

    public drop(control, item?): void {
        this._dropParam = null;

        this._controls.push(control);

        this._dropOverData = null;

        control.droppable({
            containment: false,
            tolerance: 'pointer',
            greedy: this._param.greedy,
            over: (ui, data) => {
                this.fixTarget(ui);
                this._dropOverData = data;

                this._dropParam = $.extend(data.helper.data('dragObjects'), {
                    objectTo: item || null,
                    event: ui
                });

                const checkScopes = this._checkScopes(this._dropParam.scopes);
                if (checkScopes && this._dropParam && this._dropParam.dataObjects) {
                    this._body.on(pointerHelper.pointerMoveEventName, this._dropOverFunction);
                    this._onDragOver(this._dropParam);
                }

                return false;
            },
            out: (ui, data) => {
                this._body.off(pointerHelper.pointerMoveEventName, this._dropOverFunction);

                const checkScopes = this._dropParam && this._checkScopes(this._dropParam.scopes);
                if (checkScopes && this._dropParam && this._dropParam.dataObjects) {
                    this._onDragOut(this._dropParam);
                }
                this._dropOverData = null;
            },
            drop: (ui, data) => {
                this._body.off(pointerHelper.pointerMoveEventName, this._dropOverFunction);
                if (this._dropParam) {
                    const checkScopes = this._checkScopes(this._dropParam.scopes);
                    if (checkScopes && this._dropParam.dataObjects) {
                        this._onDrop(this._dropParam);
                    }
                }
                this._removeCursorStyle();
                this._dropOverData = null;
            }
        });

        this._dropOverFunction = e => {
            this._dropParam.event = e;
            this._onDropOver(this._dropParam);

            if (
                this._dropOverData &&
                this._param.scrollArea &&
                !$.contains(this._param.scrollArea[0], this._dropOverData.draggable[0]) &&
                this._param.iScroll
            ) {
                this._autoScroll(control, this._dropOverData, true);
            }
        };
    }

    public dropOver(e, dropParam): void {
        dropParam.event = e;
        this._onDropOver(dropParam);
    }

    public scroll(control, scroll): void {}

    public setScrollArea(scrollArea): void {
        this._param.scrollArea = scrollArea;
    }

    public setIScroll(iScroll): void {
        this._param.iScroll = iScroll;
    }

    public offDropOverFunction(): void {
        this._body.off(pointerHelper.pointerMoveEventName, this._dropOverFunction);
    }

    // if you want to customize drag container, please override all this functions
    public createDraggingControl = (dragObjects): JQuery | JQuery[] => {
        if (Array.isArray(dragObjects.dataObjects) && dragObjects.dataObjects.length > 1) {
            this._dragControl = $('<div/>').toggleClass('drag-control disable', this._param.showContainer);
            _(dragObjects.dataObjects).forEach(i => this._dragControl.append($('<div/>').text(i.name)));
        } else {
            this._dragControl = $('<span class="drag-control disable"/>').text(dragObjects.objectFrom.name);
        }

        this._dragDescription = $('<div class="drag-description text-no-wrap"/>').prependTo(this._dragControl);

        return this._dragControl;
    };

    public setDragDescription(html): void {
        if (this._dragDescription) {
            !html ? this._dragDescription.hide() : this._dragDescription.show().text(html);
        }
    }

    public setDragDescriptionHTML(html): void {
        if (this._dragDescription) {
            !html ? this._dragDescription.hide() : this._dragDescription.show().html(html);
        }
    }

    public toggleDragDescription(data): void {
        if (data === true) {
            this._dragControl.removeClass('disable');
        } else if (data === false) {
            this._dragControl.addClass('disable');
        }
    }

    public hideDragDescription(): void {
        this._root.hide();
    }

    public showDragDescription(): void {
        this._root.show();
    }

    // end of override

    public getDraggingControl(): JQuery {
        return this._root;
    }

    public enableRefreshPosition(control): void {
        control.draggable('option', 'refreshPositions', true);
    }

    public disableRefreshPosition(control): void {
        control.draggable('option', 'refreshPositions', false);
    }

    public destroy(): void {
        if (this._controls) {
            this._controls.forEach(control => {
                control[0].draggable && control.draggable('destroy');
                control[0].droppable && control.droppable('destroy');
            });
            this._controls = null;
        }

        this.offDropOverFunction();

        super.destroy();
        destroyEvents(this);
    }

    public getDragContaiment(): JQuery | string {
        return this._param.dragContainment;
    }

    public fixTarget(e): void {
        if (e.originalEvent && e.originalEvent.originalEvent && !e.originalEvent.originalEvent.isTrusted) {
            e.target = e.originalEvent.target = pointerHelper.getElementFromPosition(e.originalEvent);
        }
    }

    /* Public events */
    public onDrag(fn): void {
        _.isFunction(fn) && (this._onDrag = fn);
    }

    public onDragOver(fn): void {
        _.isFunction(fn) && (this._onDragOver = fn);
    }

    public onDragOut(fn): void {
        _.isFunction(fn) && (this._onDragOut = fn);
    }

    public onDragStart(fn): void {
        _.isFunction(fn) && (this._onDragStart = fn);
    }

    public onDragStop(fn): void {
        _.isFunction(fn) && (this._onDragStop = fn);
    }

    public onPreDrag(fn): void {
        _.isFunction(fn) && (this._onPreDrag = fn);
    }

    public onDrop(fn): void {
        _.isFunction(fn) && (this._onDrop = fn);
    }

    public onDropOver(fn): void {
        _.isFunction(fn) && (this._onDropOver = fn);
    }

    public refreshPosition(): void {
        if (this._dropOverData) {
            this.enableRefreshPosition(this._dropOverData.draggable);
        }
    }
}
